import React, { useId, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const FORM_NAME = 'prepxai-waitlist';
const DEFAULT_WAITLIST_ENDPOINT = 'https://formsubmit.co/ajax/contact@prepxai.in';

const initialFields = {
  name: '',
  email: '',
  companyWebsite: '',
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const toneClasses = {
  light: {
    shell: 'border-[var(--lp-line)] bg-white text-[var(--lp-ink)] shadow-sm dark:bg-[#111216]',
    label: 'text-[var(--lp-ink-soft)]',
    input: 'border-[var(--lp-line)] bg-white text-[var(--lp-ink)] placeholder:text-[var(--lp-ink-soft)] focus:border-[var(--lp-accent)] dark:bg-[#08080a]',
    helper: 'text-[var(--lp-ink-soft)]',
    success: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
    error: 'border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300',
  },
  dark: {
    shell: 'border-white/10 bg-white/[0.06] text-white',
    label: 'text-white/60',
    input: 'border-white/10 bg-white/[0.08] text-white placeholder:text-white/35 focus:border-[var(--lp-accent)]',
    helper: 'text-white/50',
    success: 'border-emerald-300/30 bg-emerald-300/10 text-emerald-200',
    error: 'border-red-300/30 bg-red-300/10 text-red-200',
  },
};

const WaitlistForm = ({ tone = 'light', compact = false, className = '' }) => {
  const generatedId = useId();
  const formId = `waitlist-${generatedId.replace(/:/g, '')}`;
  const [fields, setFields] = useState(initialFields);
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const styles = toneClasses[tone] || toneClasses.light;

  const endpoint = useMemo(() => {
    return (import.meta.env.VITE_WAITLIST_FORM_ENDPOINT || DEFAULT_WAITLIST_ENDPOINT).trim();
  }, []);

  const updateField = (event) => {
    const { name, value } = event.target;
    setFields((current) => ({ ...current, [name]: value }));
  };

  const persistLocalReceipt = () => {
    try {
      localStorage.setItem(
        'prepxai_waitlist_signup',
        JSON.stringify({
          email: fields.email,
          name: fields.name,
          submittedAt: new Date().toISOString(),
        })
      );
    } catch {
      // Local storage is a nice-to-have receipt only.
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage('');

    if (fields.companyWebsite) {
      setStatus('success');
      return;
    }

    if (!emailPattern.test(fields.email.trim())) {
      setStatus('error');
      setMessage('Enter a valid email address.');
      return;
    }

    setStatus('loading');

    const payload = new FormData();
    payload.append('form-name', FORM_NAME);
    payload.append('name', fields.name.trim());
    payload.append('email', fields.email.trim());
    payload.append('source', 'prepxai waitlist landing page');
    payload.append('_subject', 'New prepxai waitlist signup');
    payload.append('_template', 'table');
    payload.append('_captcha', 'false');

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: payload,
      });

      if (!response.ok) {
        throw new Error('Waitlist submission failed');
      }

      persistLocalReceipt();
      setFields(initialFields);
      setStatus('success');
      setMessage("You're on the waitlist. We will reach out before early access opens.");
    } catch (error) {
      console.error(error);
      setStatus('error');
      setMessage('Submission failed. Please try again in a moment.');
    }
  };

  return (
    <form
      id={compact ? undefined : 'waitlist'}
      name={FORM_NAME}
      method="POST"
      onSubmit={handleSubmit}
      className={`w-full rounded-lg border p-4 sm:p-5 ${styles.shell} ${className}`}
    >
      <input type="hidden" name="form-name" value={FORM_NAME} />
      <div className="hidden" aria-hidden="true">
        <label htmlFor={`${formId}-companyWebsite`}>Company website</label>
        <input
          id={`${formId}-companyWebsite`}
          name="companyWebsite"
          tabIndex={-1}
          autoComplete="off"
          value={fields.companyWebsite}
          onChange={updateField}
        />
      </div>

      <div className={compact ? 'grid gap-3 sm:grid-cols-[1fr_1fr_auto]' : 'grid gap-3 sm:grid-cols-2'}>
        <div>
          <label htmlFor={`${formId}-name`} className={`mb-1.5 block text-[11px] font-medium ${styles.label}`}>
            Name
          </label>
          <input
            id={`${formId}-name`}
            name="name"
            type="text"
            value={fields.name}
            onChange={updateField}
            placeholder="Your name"
            className={`h-11 w-full rounded-md px-3 text-sm outline-none transition-colors ${styles.input}`}
          />
        </div>

        <div>
          <label htmlFor={`${formId}-email`} className={`mb-1.5 block text-[11px] font-medium ${styles.label}`}>
            Email
          </label>
          <input
            id={`${formId}-email`}
            name="email"
            type="email"
            required
            value={fields.email}
            onChange={updateField}
            placeholder="you@example.com"
            className={`h-11 w-full rounded-md px-3 text-sm outline-none transition-colors ${styles.input}`}
          />
        </div>

        <button
          type="submit"
          disabled={status === 'loading'}
          className={`inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[var(--lp-accent)] px-5 text-sm font-semibold text-white transition-colors hover:bg-[var(--lp-accent)]/90 disabled:cursor-not-allowed disabled:opacity-70 ${
            compact ? 'self-end whitespace-nowrap' : 'sm:col-span-2'
          }`}
        >
          {status === 'loading' ? (
            <>
              <Icon name="Loader2" size={16} className="animate-spin" />
              Joining
            </>
          ) : (
            <>
              Join waitlist
              <Icon name="ArrowRight" size={16} />
            </>
          )}
        </button>
      </div>

      <p className={`mt-3 text-[11px] leading-5 ${styles.helper}`}>
        Early access only. We will use this email for prepxai launch updates and waitlist access.
      </p>
      <p className={`mt-1.5 text-[11px] leading-5 ${styles.helper}`}>
        By joining the waitlist, you agree to our{' '}
        <Link to="/privacy" className="font-semibold text-[var(--lp-accent)] hover:underline">
          Privacy Policy
        </Link>
        ,{' '}
        <Link to="/terms" className="font-semibold text-[var(--lp-accent)] hover:underline">
          Terms of Service
        </Link>
        , and{' '}
        <Link to="/beta-disclaimer" className="font-semibold text-[var(--lp-accent)] hover:underline">
          Beta Disclaimer
        </Link>
        .
      </p>

      {message && (
        <div
          className={`mt-3 rounded-md border px-3 py-2 text-xs ${
            status === 'success' ? styles.success : styles.error
          }`}
          role="status"
        >
          {message}
        </div>
      )}
    </form>
  );
};

export default WaitlistForm;
